import React, { useState, useEffect } from "react";
import {
  useHistory,
  useParams,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import {
  Home,
  Settings,
  Menu,
  X,
  BarChart3,
  ChevronLeft,
  LogOut,
  Calculator,
} from "lucide-react";
import AccountAntHome from "./AccountantComps/AccountAntHome";
import AccountAntSetting from "./AccountantComps/AccountAntSetting";
import { useAppContext } from "../AppContext";
import { useTranslation } from "react-i18next";

export default function AccountantMainPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { AccountantId } = useParams();
  const { Lang } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";

  console.log("Current Lang:", Lang, "isRTL:", isRTL); // Debug log

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const user = localStorage.getItem("user");
    if (!user) {
      history.replace("/login");
      return;
    }
    const parsedUser = JSON.parse(user);

    // Verify user is an accountant
    if (parsedUser.type !== 3) {
      history.replace("/login");
      return;
    }

    setUserData(parsedUser);
  }, [history]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    history.replace("/login");
  };

  const menuItems = [
    {
      path: `/accountant-main-page/${AccountantId}/home`,
      icon: Home,
      label: t("home"),
      color: "text-blue-600",
    },
    {
      path: `/accountant-main-page/${AccountantId}/settings`,
      icon: Settings,
      label: t("settings"),
      color: "text-gray-600",
    },
  ];

  const isActive = (path) => location.pathname === path;

  // Redirect to home by default
  useEffect(() => {
    if (location.pathname === `/accountant-main-page/${AccountantId}`) {
      history.replace(`/accountant-main-page/${AccountantId}/home`);
    }
  }, [location.pathname, AccountantId, history]);

  return (
    <div
      className="flex h-screen bg-gray-50 overflow-hidden"
      style={{ direction: isRTL ? "rtl" : "ltr" }}
    >
      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        
        .animate-slide-in-left {
          animation: slideInLeft 0.3s ease-out;
        }
        
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>

      {/* Sidebar - Desktop */}
      <aside
        className={`hidden lg:flex flex-col bg-white transition-all duration-300 ${
          sidebarOpen ? "w-64" : "w-20"
        }`}
        style={{
          borderRight: isRTL ? "none" : "1px solid #e5e7eb",
          borderLeft: isRTL ? "1px solid #e5e7eb" : "none",
        }}
      >
        {/* Logo Section */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {sidebarOpen ? (
            <>
              <div
                className="flex items-center"
                style={{
                  gap: "8px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <BarChart3 className="w-8 h-8 text-blue-600" />
                <span className="text-xl font-bold text-gray-900">
                  NeholEsek
                </span>
              </div>
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition"
              >
                <ChevronLeft
                  className="w-5 h-5 text-gray-600 transition-transform"
                  style={{
                    transform: isRTL
                      ? sidebarOpen
                        ? "rotate(0deg)"
                        : "rotate(180deg)"
                      : sidebarOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </>
          ) : (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-full flex justify-center p-2 rounded-lg hover:bg-gray-100 transition"
            >
              <BarChart3 className="w-8 h-8 text-blue-600" />
            </button>
          )}
        </div>

        {/* User Info */}
        {sidebarOpen && userData && (
          <div className="p-4 border-b border-gray-200">
            <div
              className="flex items-center"
              style={{
                gap: "12px",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Calculator className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-semibold text-gray-900 truncate"
                  style={{ textAlign: isRTL ? "right" : "left" }}
                >
                  {userData.name || t("user")}
                </p>
                <p
                  className="text-xs text-gray-500"
                  style={{ textAlign: isRTL ? "right" : "left" }}
                >
                  {t("accountantAccount")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Collapsed User Avatar */}
        {!sidebarOpen && userData && (
          <div className="p-4 border-b border-gray-200 flex justify-center">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-green-600" />
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <div key={item.path} className="relative group">
                <button
                  onClick={() => history.push(item.path)}
                  className={`w-full flex items-center ${
                    sidebarOpen ? "" : "justify-center"
                  } px-4 py-3 rounded-lg transition-all ${
                    active
                      ? "bg-blue-50 text-blue-600 shadow-sm"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  style={{
                    gap: sidebarOpen ? "12px" : "0",
                    flexDirection: isRTL ? "row-reverse" : "row",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  <Icon
                    className={`w-5 h-5 flex-shrink-0 ${
                      active ? "text-blue-600" : item.color
                    }`}
                  />
                  {sidebarOpen && (
                    <span className="font-medium">{item.label}</span>
                  )}
                </button>

                {/* Tooltip for collapsed sidebar */}
                {!sidebarOpen && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none"
                    style={{
                      left: isRTL ? "auto" : "100%",
                      right: isRTL ? "100%" : "auto",
                      marginLeft: isRTL ? "0" : "8px",
                      marginRight: isRTL ? "8px" : "0",
                    }}
                  >
                    {item.label}
                    <div
                      className="absolute top-1/2 -translate-y-1/2 border-4 border-transparent"
                      style={{
                        right: isRTL ? "auto" : "100%",
                        left: isRTL ? "100%" : "auto",
                        borderRightColor: isRTL ? "transparent" : "#1f2937",
                        borderLeftColor: isRTL ? "#1f2937" : "transparent",
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-200">
          <div className="relative group">
            <button
              onClick={handleLogout}
              className={`w-full flex items-center ${
                sidebarOpen ? "" : "justify-center"
              } px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition`}
              style={{
                gap: sidebarOpen ? "12px" : "0",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && (
                <span className="font-medium">{t("logout")}</span>
              )}
            </button>

            {/* Tooltip for collapsed sidebar */}
            {!sidebarOpen && (
              <div
                className="absolute top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none"
                style={{
                  left: isRTL ? "auto" : "100%",
                  right: isRTL ? "100%" : "auto",
                  marginLeft: isRTL ? "0" : "8px",
                  marginRight: isRTL ? "8px" : "0",
                }}
              >
                {t("logout")}
                <div
                  className="absolute top-1/2 -translate-y-1/2 border-4 border-transparent"
                  style={{
                    right: isRTL ? "auto" : "100%",
                    left: isRTL ? "100%" : "auto",
                    borderRightColor: isRTL ? "transparent" : "#1f2937",
                    borderLeftColor: isRTL ? "#1f2937" : "transparent",
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className="lg:hidden fixed inset-y-0 w-64 bg-white border-gray-200 z-50 transform transition-transform duration-300"
        style={{
          left: isRTL ? "auto" : 0,
          right: isRTL ? 0 : "auto",
          borderRight: isRTL ? "none" : "1px solid #e5e7eb",
          borderLeft: isRTL ? "1px solid #e5e7eb" : "none",
          transform: mobileMenuOpen
            ? "translateX(0)"
            : isRTL
              ? "translateX(100%)"
              : "translateX(-100%)",
        }}
      >
        {/* Logo Section */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          <div
            className="flex items-center"
            style={{ gap: "8px", flexDirection: isRTL ? "row-reverse" : "row" }}
          >
            <BarChart3 className="w-8 h-8 text-blue-600" />
            <span className="text-xl font-bold text-gray-900">NeholEsek</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* User Info */}
        {userData && (
          <div className="p-4 border-b border-gray-200">
            <div
              className="flex items-center"
              style={{
                gap: "12px",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Calculator className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p
                  className="text-sm font-semibold text-gray-900"
                  style={{ textAlign: isRTL ? "right" : "left" }}
                >
                  {userData.name || t("user")}
                </p>
                <p
                  className="text-xs text-gray-500"
                  style={{ textAlign: isRTL ? "right" : "left" }}
                >
                  {t("accountantAccount")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => {
                  history.push(item.path);
                  setMobileMenuOpen(false);
                }}
                className={`w-full flex items-center px-4 py-3 rounded-lg transition-all ${
                  active
                    ? "bg-blue-50 text-blue-600 shadow-sm"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                style={{
                  gap: "12px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                <Icon
                  className={`w-5 h-5 ${active ? "text-blue-600" : item.color}`}
                />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition"
            style={{
              gap: "12px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">{t("logout")}</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8">
          <div
            className="flex items-center"
            style={{
              gap: "16px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
            <h1
              className="text-xl font-bold text-gray-900"
              style={{ textAlign: isRTL ? "right" : "left" }}
            >
              {menuItems.find((item) => isActive(item.path))?.label ||
                t("dashboard")}
            </h1>
          </div>

          {/* Header Actions */}
          <div
            className="flex items-center"
            style={{
              gap: "16px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            {/* Notification Bell */}
            <button className="relative p-2 rounded-lg hover:bg-gray-100 transition">
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <span
                className="absolute w-2 h-2 bg-red-500 rounded-full"
                style={{
                  top: "4px",
                  right: isRTL ? "auto" : "4px",
                  left: isRTL ? "4px" : "auto",
                }}
              />
            </button>

            {/* User Avatar - Mobile */}
            <div className="lg:hidden w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <Calculator className="w-4 h-4 text-green-600" />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <Switch>
            <Route
              path="/accountant-main-page/:AccountantId/home"
              component={AccountAntHome}
            />
            <Route
              path="/accountant-main-page/:AccountantId/settings"
              component={AccountAntSetting}
            />
          </Switch>
        </main>
      </div>
    </div>
  );
}
